import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, Upload, Image, App, Spin, Breadcrumb } from 'antd';
import { UploadOutlined, HomeOutlined } from '@ant-design/icons';
import { createPair, updatePair, getPairById, uploadImage } from '../../services/pairsService';
import consts from '../../constant';
import './Pairs.css';

const { Option } = Select;

const CreateOrUpdatePair = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { message } = App.useApp();
  const isEditMode = !!id;
  const selectedType = Form.useWatch('type', form);

  useEffect(() => {
    if (isEditMode) {
      fetchPairDetails();
    }
  }, [id]);

  const fetchPairDetails = async () => {
    setFetching(true);
    try {
      const response = await getPairById(id);
      if (response.success) {
        const pair = response.result;
        form.setFieldsValue({
          ...pair,
          type: pair.type ? pair.type.toUpperCase() : pair.type,
        });
        setImageUrl(pair.icon);
      } else {
        message.error('Failed to fetch pair details');
      }
    } catch (error) {
      message.error('Error fetching pair details');
    } finally {
      setFetching(false);
    }
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const response = await uploadImage(file);
      if (response.success) {
        setImageUrl(response.result);
        message.success('Image uploaded successfully');
      } else {
        message.error('Failed to upload image');
      }
    } catch (error) {
      message.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const data = {
        ...values,
        icon: imageUrl,
        pair_symbol: `${values.base_asset_name}_${values.market_asset_name}`,
        original_symbol: `${values.base_asset_name}${values.market_asset_name}`,
      };

      // ✅ Clean unused configs
      if (data.type !== 'FOREX') {
        delete data.forex_config;
      }

      if (data.type !== 'COMMODITIES') {
        delete data.commodity_config;
      }

      let response;
      if (isEditMode) {
        response = await updatePair(id, data);
      } else {
        response = await createPair(data);
      }
      if (response.success) {
        message.success(response.message || `${isEditMode ? 'Updated' : 'Created'} pair successfully`);
        navigate(`/${consts.route}/pairs`);
      } else {
        console.log("🚀 ~ onFinish ~ response:", response)
        message.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} pair`);
      }
    } catch (error) {
      message.error(`Error ${isEditMode ? 'updating' : 'creating'} pair`);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      handleImageUpload(file);
      return false; // Prevent default upload
    },
    showUploadList: false,
  };

  if (fetching) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  console.log("🚀 ~ CreateOrUpdatePair ~ selectedType:", selectedType)
  return (
    <div style={{ padding: '20px' }}>
      <Breadcrumb
      className="custom-breadcrumb"
        style={{ marginBottom: '20px',color:"fff" }}
        items={[
          {
            title: (
              <>
                <HomeOutlined />
                <span>Dashboard</span>
              </>
            ),
            href: `/${consts.route}/dashboard`,
          },
          {
            title: 'Pairs',
            href: `/${consts.route}/pairs`,
          },
          {
            title: isEditMode ? 'Edit Pair' : 'Create Pair',
          },
        ]}
      />
      <h2>{isEditMode ? 'Edit Pair' : 'Create Pair'}</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          status: true,
          type: 'FUTURE',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item label="Icon">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} loading={uploading}>
                  Upload Image
                </Button>
              </Upload>
              {imageUrl && <Image width={100} src={imageUrl} style={{ marginTop: '10px' }} />}
            </div>
          </Form.Item>
          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select disabled={isEditMode} className='pair-type-select'>
              <Option value="FUTURE">Future</Option>
              <Option value="CRYPTO">Crypto</Option>
              <Option value="FOREX">Forex</Option>
              <Option value="COMMODITIES">Commodity</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Base Asset Name"
            name="base_asset_name"
            normalize={(value) => value?.toUpperCase()}
            rules={[{ required: true, message: 'Please enter base asset name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Market Asset Name"
            name="market_asset_name"
            normalize={(value) => value?.toUpperCase()}
            rules={[{ required: true, message: 'Please enter market asset name' }]}
          >
            <Input />
          </Form.Item>


          {/* <Form.Item
            label="Buy Trade Commission (%)"
            name="buy_trade_commission"
            rules={[{ required: true, message: 'Please enter buy trade commission' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Sell Trade Commission (%)"
            name="sell_trade_commission"
            rules={[{ required: true, message: 'Please enter sell trade commission' }]}
          > */}
          {/* <Input type="number" />
        </Form.Item> */}
          <Form.Item
            label="Price Precision"
            name="pricePrecision"
            rules={[{ required: true, message: 'Please enter price precision' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Quantity Precision"
            name="quantityPrecision"
            rules={[{ required: true, message: 'Please enter quantity precision' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Tick Size"
            name="tickSize"
            rules={[{ required: true, message: 'Enter tick size' }]}
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item
            label="Step Size"
            name="stepSize"
            rules={[{ required: true, message: 'Enter step size' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Base Asset Precision"
            name="baseAssetPrecision"
            rules={[{ required: true, message: 'Please enter base asset precision' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Quote Asset Precision"
            name="quoteAssetPrecision"
            rules={[{ required: true, message: 'Please enter quote asset precision' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Min Notional"
            name="minNotional"
            rules={[{ required: true, message: 'Please enter min notional' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Min Qty"
            name="minQuantity"
            rules={[{ required: true, message: 'Please enter min qty' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Min Price"
            name="minPrice"
            rules={[{ required: true, message: 'Please enter min price' }]}
          >
            <Input type="number" />
          </Form.Item>

          {/* {selectedType === 'FOREX' && (
            <>

              <Form.Item
                label="Pip Size"
                name={['forex_config', 'pip_size']}
                rules={[{ required: true, message: 'Enter pip size' }]}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item
                label="Pip Value"
                name={['forex_config', 'pip_value']}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item label="Lot Size" name="lot_size">
                <Input type="number" />
              </Form.Item>

              <Form.Item label="Min Lot" name="min_lot">
                <Input type="number" />
              </Form.Item>

              <Form.Item label="Max Lot" name="max_lot">
                <Input type="number" />
              </Form.Item>

              <Form.Item label="Lot Step" name="lot_step">
                <Input type="number" />
              </Form.Item>
            </>
          )}
          {selectedType === 'COMMODITIES' && (
            <>

              <Form.Item
                label="Contract Size"
                name={['commodity_config', 'contract_size']}
                rules={[{ required: true, message: 'Enter contract size' }]}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item
                label="Tick Value"
                name={['commodity_config', 'tick_value']}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item
                label="Delivery Type"
                name={['commodity_config', 'delivery_type']}
              >
                <Select>
                  <Option value="PHYSICAL">Physical</Option>
                  <Option value="CASH">Cash</Option>
                </Select>
              </Form.Item>
            </>
          )}
          {selectedType === 'FUTURE' && (
            <>

              <Form.Item label="Leverage" name="leverage">
                <Input type="number" />
              </Form.Item>
            </>
          )} */}

          <Form.Item label="Status" name="status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditMode ? 'Update Pair' : 'Create Pair'}
            </Button>
            <Button style={{ marginLeft: '10px' }} onClick={() => navigate(`/${consts.route}/pairs`)}>
              Cancel
            </Button>
          </Form.Item>
        </div>
      </Form >
    </div >
  );
};

export default CreateOrUpdatePair;